import React from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Switch,
  FormControlLabel,
  RadioGroup,
  Radio,
  Button,
  Divider,
  Grid,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  Chip,
  Alert,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Palette,
  ViewSidebar,
  ViewDay,
  RestartAlt,
  Check,
  Circle,
} from '@mui/icons-material';
import { useLayout } from '../contexts/LayoutContext';
import { useTheme as useAppTheme } from '../contexts/AppThemeProvider';
import { PageHeader } from '../components/ui/PageHeader';

interface ThemePreset {
  id: string;
  name: string;
  description: string;
  primary: string;
  secondary: string;
  background: string;
  primaryLight?: string;
  secondaryLight?: string;
}

const themePresets: ThemePreset[] = [
  {
    id: 'modern',
    name: 'Modern',
    description: 'Clean and contemporary design',
    primary: '#6366F1',
    secondary: '#8B5CF6',
    background: '#0F0F1A',
    primaryLight: '#10B981',  // Green for light mode
    secondaryLight: '#059669',
  },
  {
    id: 'classic',
    name: 'Classic Blue',
    description: 'Traditional enterprise look',
    primary: '#1976D2',
    secondary: '#0D47A1',
    background: '#F5F5F5',
    primaryLight: '#3099c5',  // Classic blue for light mode
    secondaryLight: '#2b3e7d',
  },
  {
    id: 'custom',
    name: 'Custom',
    description: 'Configure your own theme',
    primary: '#000000',
    secondary: '#666666',
    background: '#FFFFFF',
    primaryLight: '#000000',
    secondaryLight: '#666666',
  },
];

const Settings: React.FC = () => {
  const theme = useTheme();
  const { mode, toggleMode } = useAppTheme();
  const { preferences, setLayoutMode, setThemePreset, updatePreferences, resetToDefaults } = useLayout();
  const [saveMessage, setSaveMessage] = React.useState(false);

  const handleLayoutChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setLayoutMode(event.target.value as 'top-nav' | 'sidebar');
    showSaveMessage();
  };

  const handleThemePresetChange = (presetId: string) => {
    setThemePreset(presetId as 'modern' | 'classic' | 'custom');
    showSaveMessage();
  };

  const handleReset = () => {
    resetToDefaults();
    showSaveMessage();
  };

  const showSaveMessage = () => {
    setSaveMessage(true);
    setTimeout(() => setSaveMessage(false), 3000);
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <PageHeader
        title="Settings"
        subtitle="Customize your app experience"
      />

      {saveMessage && (
        <Alert
          severity="success"
          sx={{ mb: 3 }}
          onClose={() => setSaveMessage(false)}
        >
          Settings saved successfully!
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ViewSidebar />
              Layout Mode
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Choose how navigation is displayed
            </Typography>

            <RadioGroup
              value={preferences.layoutMode}
              onChange={handleLayoutChange}
            >
              <Card
                sx={{
                  mb: 2,
                  border: preferences.layoutMode === 'top-nav' ? `2px solid ${theme.palette.primary.main}` : '1px solid',
                  borderColor: preferences.layoutMode === 'top-nav' ? theme.palette.primary.main : theme.palette.divider,
                  backgroundColor: preferences.layoutMode === 'top-nav' ? alpha(theme.palette.primary.main, 0.05) : 'transparent',
                }}
              >
                <CardContent>
                  <FormControlLabel
                    value="top-nav"
                    control={<Radio />}
                    label={
                      <Box>
                        <Typography variant="subtitle1" fontWeight={600}>
                          Top Navigation
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Modern tabs across the top with icons (current design)
                        </Typography>
                      </Box>
                    }
                  />
                </CardContent>
              </Card>

              <Card
                sx={{
                  border: preferences.layoutMode === 'sidebar' ? `2px solid ${theme.palette.primary.main}` : '1px solid',
                  borderColor: preferences.layoutMode === 'sidebar' ? theme.palette.primary.main : theme.palette.divider,
                  backgroundColor: preferences.layoutMode === 'sidebar' ? alpha(theme.palette.primary.main, 0.05) : 'transparent',
                }}
              >
                <CardContent>
                  <FormControlLabel
                    value="sidebar"
                    control={<Radio />}
                    label={
                      <Box>
                        <Typography variant="subtitle1" fontWeight={600}>
                          Sidebar Navigation
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Traditional left sidebar with collapsible menu
                        </Typography>
                      </Box>
                    }
                  />
                </CardContent>
              </Card>
            </RadioGroup>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Palette />
              Theme Preset
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Select a color scheme for the interface
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {themePresets.map((preset) => (
                <Card
                  key={preset.id}
                  sx={{
                    cursor: 'pointer',
                    border: preferences.themePreset === preset.id ? `2px solid ${theme.palette.primary.main}` : '1px solid',
                    borderColor: preferences.themePreset === preset.id ? theme.palette.primary.main : theme.palette.divider,
                    backgroundColor: preferences.themePreset === preset.id ? alpha(theme.palette.primary.main, 0.05) : 'transparent',
                    transition: 'all 0.2s',
                    '&:hover': {
                      borderColor: theme.palette.primary.main,
                      transform: 'translateY(-2px)',
                    },
                  }}
                  onClick={() => handleThemePresetChange(preset.id)}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle1" fontWeight={600}>
                          {preset.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {preset.description}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Tooltip title="Primary color">
                          <Circle sx={{ 
                            color: mode === 'light' && preset.primaryLight ? preset.primaryLight : preset.primary, 
                            fontSize: 24 
                          }} />
                        </Tooltip>
                        <Tooltip title="Secondary color">
                          <Circle sx={{ 
                            color: mode === 'light' && preset.secondaryLight ? preset.secondaryLight : preset.secondary, 
                            fontSize: 24 
                          }} />
                        </Tooltip>
                      </Box>
                      {preferences.themePreset === preset.id && (
                        <Check sx={{ color: theme.palette.primary.main, ml: 2 }} />
                      )}
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>

            {preferences.themePreset === 'custom' && (
              <Alert severity="info" sx={{ mt: 2 }}>
                Custom theme configuration will be available after you provide the colors and fonts from the old app.
              </Alert>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Additional Options
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={mode === 'dark'}
                    onChange={toggleMode}
                  />
                }
                label="Dark Mode"
              />

              <Divider />

              <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                <Button
                  variant="outlined"
                  startIcon={<RestartAlt />}
                  onClick={handleReset}
                >
                  Reset to Defaults
                </Button>
              </Box>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default Settings;